import type { PviGraphWithSpans } from '../../editor/pviTypes';
import styles from './PviGraphPanel.module.css';

interface ValueTableViewProps {
  graph: PviGraphWithSpans;
}

export function ValueTableView({ graph }: ValueTableViewProps) {
  if (graph.nodes.length === 0) {
    return (
      <div className={styles.emptyState}>
        <p>No value operations to display</p>
      </div>
    );
  }

  // Build steps: follow topological order (nodes are already in order from the compiler)
  const steps = graph.nodes.map((node, idx) => {
    const inEdges = graph.edges.filter(e => e.destination === node.id);

    const fromParty = node.party.type === 'account' ? node.party.address : '—';
    const sourceNodes = inEdges.map(e => {
      const src = graph.nodes.find(n => n.id === e.source);
      return src ? `#${src.id} (${src.nodeType})` : `#${e.source}`;
    });
    const toLabel = node.party.type === 'account' ? node.party.address : '—';

    return {
      step: idx + 1,
      operation: node.nodeType,
      from: sourceNodes.length > 0 ? sourceNodes.join(', ') : fromParty,
      to: toLabel,
      asset: node.assetId || '—',
      amount: node.amount !== '0' ? node.amount : '—',
      label: node.label,
    };
  });

  return (
    <div className={styles.tableContainer}>
      <table className={styles.valueTable}>
        <thead>
          <tr>
            <th>Step</th>
            <th>Operation</th>
            <th>From</th>
            <th>To</th>
            <th>Asset</th>
            <th>Amount</th>
          </tr>
        </thead>
        <tbody>
          {steps.map(s => (
            <tr key={s.step}>
              <td className={styles.stepCell}>{s.step}</td>
              <td>
                <span className={`${styles.opBadge} ${styles[`op${s.operation}`]}`}>
                  {s.operation}
                </span>
              </td>
              <td className={styles.addrCell} title={s.from}>{truncAddr(s.from)}</td>
              <td className={styles.addrCell} title={s.to}>{truncAddr(s.to)}</td>
              <td>{s.asset}</td>
              <td className={styles.amountCell}>{s.amount}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function truncAddr(addr: string): string {
  if (addr.length > 16) return `${addr.slice(0, 8)}...${addr.slice(-4)}`;
  return addr;
}
